import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { TranslateModule } from '@ngx-translate/core';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { SearchInputComponent, TableComponent, FilledButtonComponent, TextButtonComponent } from '@custom-components';
import { TableColumnConfig } from '@core/models/skeleton.model';
import { DeviceService } from '@core/services';
import { DeviceStore } from '@root/app/store/device.store';

export interface DeviceChip {
  id: number;
  label: string;
}

export interface DeviceSelectionDialogData {
  initialSelection: DeviceChip[];
}

// Widths intentionally leave room for the fixed-width select column (3rem, see
// table.component.html) — summing the text columns to 100% squeezes it to zero width.
const DEVICE_SELECTION_COLUMNS: TableColumnConfig[] = [
  { field: 'deviceLDN', label: 'dataExport.deviceSelectionDialog.columns.ldn', width: '35%', bold: true },
  { field: 'deviceName', label: 'dataExport.deviceSelectionDialog.columns.name', width: '55%' },
];

@Component({
  selector: 'xemex-device-selection-dialog',
  standalone: true,
  imports: [
    MatDialogModule,
    TranslateModule,
    SearchInputComponent,
    TableComponent,
    FilledButtonComponent,
    TextButtonComponent,
  ],
  providers: [DeviceStore],
  templateUrl: './device-selection-dialog.component.html',
  styleUrl: './device-selection-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeviceSelectionDialogComponent {
  private readonly dialogRef = inject<MatDialogRef<DeviceSelectionDialogComponent, DeviceChip[]>>(MatDialogRef);
  private readonly data = inject<DeviceSelectionDialogData>(MAT_DIALOG_DATA);
  private readonly destroyRef = inject(DestroyRef);
  private readonly deviceService = inject(DeviceService);
  protected readonly deviceStore = inject(DeviceStore);

  protected readonly columns = DEVICE_SELECTION_COLUMNS;

  protected readonly searchTerm = signal('');
  private readonly search$ = new Subject<string>();

  protected readonly tableData = computed(
    () => (this.deviceStore.deviceData()?.devices ?? []) as unknown as Record<string, unknown>[],
  );
  protected readonly totalCount = computed(() => this.deviceStore.deviceData()?.numberOfItems ?? 0);

  /** Keyed on device id (string) rather than a plain Set so a device selected on an earlier
   *  page keeps its label once that page is no longer loaded. */
  private readonly selectedById = signal<Map<string, DeviceChip>>(
    new Map(this.data.initialSelection.map(chip => [String(chip.id), chip])),
  );
  protected readonly selectedKeys = computed(() => new Set<string | number>(this.selectedById().keys()));
  protected readonly selectedCount = computed(() => this.selectedById().size);
  protected readonly selectingAll = signal(false);

  protected readonly selectionKey = (row: Record<string, unknown>): string => String(row['deviceId']);

  constructor() {
    this.deviceStore.getDevicesMethod(0);

    this.search$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        this.searchTerm.set(value);
        this.deviceStore.getDevicesFilterMethod({ deviceLDN: value });
      });
  }

  protected onSearchChange(value: string): void {
    this.search$.next(value);
  }

  protected onPageChange(event: PageEvent): void {
    this.deviceStore.getDevicesMethod(event.pageIndex);
  }

  /** New keys can only come from rows in the currently-loaded page, so resolving their label
   *  from the live device list is always safe; previously-known selections keep their label. */
  protected onSelectedKeysChange(nextKeys: Set<string | number>): void {
    const currentDevices = this.deviceStore.deviceData()?.devices ?? [];
    const byId = new Map(currentDevices.map(device => [String(device.deviceId), device]));
    const previous = this.selectedById();
    const next = new Map<string, DeviceChip>();
    nextKeys.forEach(key => {
      const id = String(key);
      const existing = previous.get(id);
      if (existing) {
        next.set(id, existing);
        return;
      }
      const device = byId.get(id);
      if (device) next.set(id, { id: Number(device.deviceId), label: device.deviceLDN });
    });
    this.selectedById.set(next);
  }

  /** {@link TableComponent}'s select-all-across-pages checkbox has no way to fetch other pages
   *  itself, so on `true` we re-fetch everything matching the current filter in one call (same
   *  large-limit pattern as the group/location fetches in data-export-page.component.ts) and
   *  select all of it; on `false` we just clear the whole selection. */
  protected onSelectAllRequested(selectAll: boolean): void {
    if (!selectAll) {
      this.selectedById.set(new Map());
      return;
    }

    const total = this.totalCount();
    if (total === 0) return;

    this.selectingAll.set(true);
    this.deviceService
      .getListDevicesFilter({ ...this.deviceStore.filter(), offset: 0, limit: total })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(data => {
        this.selectingAll.set(false);
        this.selectedById.set(
          new Map(
            (data.devices ?? []).map(device => [
              String(device.deviceId),
              { id: Number(device.deviceId), label: device.deviceLDN },
            ]),
          ),
        );
      });
  }

  protected onCancel(): void {
    this.dialogRef.close();
  }

  protected onApply(): void {
    this.dialogRef.close(Array.from(this.selectedById().values()));
  }
}
